/**
 * chipProvenance — how a value chip labels a non-measured number.
 *
 * "Ion Unchained" (Ion Next spec 2026-10-21): a number is measured, verified
 * derived, an estimate (method + basis + why unverified) or a recommendation
 * (confidence + reason). The backend marks the last three on the chip's
 * `data_value` artifact as `provenance.kind` (native_derived.py). Only the Ion
 * Next admin page passes `provenance` to DataValueChip (MarkdownRenderer's
 * opt-in `chipProvenance`); the customer chat never does, so its chip is
 * unchanged.
 *
 * Returns null for a measured value (no recognised `kind`): the chip then
 * renders exactly the customer chip.
 */

const KINDS = new Set(['derived', 'estimate', 'recommendation'])

function refList(refs) {
  return Array.isArray(refs) && refs.length > 0 ? refs.map((r) => `V:${r}`).join(', ') : ''
}

function reasonText(reason) {
  if (!reason) return ''
  if (typeof reason === 'string') return reason
  return reason.message || reason.code || ''
}

export function chipProvenanceInfo(provenance) {
  const p = provenance && typeof provenance === 'object' ? provenance : null
  if (!p || !KINDS.has(p.kind)) return null

  let cssKind
  let chipLabel
  let ariaPrefix
  let title
  let showCheck = false
  let ariaSuffix = ''
  if (p.kind === 'derived') {
    if (p.verified === true) {
      cssKind = 'derived'
      chipLabel = 'Derived'
      ariaPrefix = 'Derived value, verified'
      title = 'Derived · verified by the server check'
      // The check mark means "verified": only a verified derived chip keeps it.
      showCheck = true
    } else {
      cssKind = 'derived-unverified'
      chipLabel = 'Derived · unverified'
      ariaPrefix = 'Derived value, not verified'
      title = 'Derived · not verified'
    }
  } else if (p.kind === 'estimate') {
    cssKind = 'estimate'
    chipLabel = 'Estimate'
    ariaPrefix = 'Estimate, not verified'
    title = 'Estimate · not verified, not measured'
    ariaSuffix = reasonText(p.reason)
  } else {
    const confidence = p.confidence ? String(p.confidence) : ''
    cssKind = 'recommendation'
    chipLabel = confidence ? `Recommendation · ${confidence}` : 'Recommendation'
    ariaPrefix = confidence ? `Recommendation, ${confidence} confidence` : 'Recommendation'
    title = confidence ? `Recommendation · ${confidence} confidence` : 'Recommendation · engineering judgement'
    ariaSuffix = reasonText(p.reason)
  }

  const lines = []
  if (p.method) lines.push(`Method: ${String(p.method).replace(/_/g, ' ')}`)
  const basis = refList(p.basis_refs)
  if (basis) lines.push(`Basis: ${basis}`)
  const roots = refList(p.root_measured_refs)
  if (roots && roots !== basis) lines.push(`Measured roots: ${roots}`)
  if (p.roots_unknown) lines.push('Measured roots: not fully traceable')
  if (Array.isArray(p.knowledge_topics) && p.knowledge_topics.length > 0) {
    lines.push(`Knowledge: ${p.knowledge_topics.join(', ')}`)
  }
  if (p.kind === 'recommendation') {
    if (p.reason) lines.push(`Reason: ${reasonText(p.reason)}`)
  } else if (p.reason) {
    const code = typeof p.reason === 'object' && p.reason.code ? ` (${p.reason.code})` : ''
    lines.push(`Why unverified: ${reasonText(p.reason)}${code}`)
  }
  if (p.recomputed && p.recomputed.value != null) {
    lines.push(`Recomputed: ${p.recomputed.value}${p.recomputed.unit ? ` ${p.recomputed.unit}` : ''}`)
  }
  if (p.caveat) lines.push(`Caveat: ${p.caveat}`)
  if (p.execution_id) lines.push(`Execution: ${p.execution_id}`)

  return { kind: p.kind, cssKind, chipLabel, ariaPrefix, ariaSuffix, title, lines, showCheck }
}
